/**
 * QuarantineAlerts Component
 *
 * Displays alerts for overdue quarantine items requiring attention.
 * Shows countdown timers and action buttons for disposal.
 *
 * User Story: US-P2-008 - Contamination Management
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { useOverdueQuarantine, useContaminationMutations } from '@/hooks/useContamination'
import { AlertTriangle, Clock, Trash2, MapPin } from 'lucide-react'

// Type display names
const TYPE_NAMES = {
  green_mold: 'Green Mold',
  black_mold: 'Black Mold',
  cobweb: 'Cobweb',
  bacterial: 'Bacterial',
  yeast: 'Yeast',
  wet_spot: 'Wet Spot',
  lipstick: 'Lipstick',
  unknown: 'Unknown',
}

/**
 * Format date as "Mon 25 Nov"
 */
function formatDate(dateStr) {
  if (!dateStr) return '-'
  const date = new Date(dateStr)
  return date.toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  })
}

export default function QuarantineAlerts({ onDispose }) {
  const { overdueItems, loading, refetch } = useOverdueQuarantine()
  const { resolveContamination, loading: mutationLoading } = useContaminationMutations()

  const handleDispose = async (item) => {
    try {
      await resolveContamination(item.event_id, 'dispose')
      refetch()
      if (onDispose) onDispose(item)
    } catch (err) {
      console.error('Error disposing item:', err)
    }
  }

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-40" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-20 w-full" />
        </CardContent>
      </Card>
    )
  }

  if (overdueItems.length === 0) {
    return null // Don't render if no overdue items
  }

  return (
    <Card className="border-red-200 bg-red-50">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-red-700">
          <AlertTriangle className="h-5 w-5" />
          Overdue Quarantine Items ({overdueItems.length})
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-red-600 mb-4">
          These items have exceeded their 14-day quarantine period and should be disposed.
        </p>

        <div className="space-y-3">
          {overdueItems.map(item => (
            <Alert key={item.event_id} variant="destructive" className="bg-white">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle className="flex items-center justify-between">
                <span>{item.container_code || 'Unknown Block'}</span>
                <Badge variant="destructive" className="ml-2">
                  {item.days_overdue} days overdue
                </Badge>
              </AlertTitle>
              <AlertDescription>
                <div className="mt-2 space-y-1 text-sm">
                  <div className="flex items-center gap-2 text-slate-600">
                    <Clock className="h-3 w-3" />
                    Quarantined: {formatDate(item.quarantine_start_date)}
                    {' '}&rarr;{' '}
                    Max: {formatDate(item.max_quarantine_date)}
                  </div>
                  {item.quarantine_location && (
                    <div className="flex items-center gap-2 text-slate-600">
                      <MapPin className="h-3 w-3" />
                      {item.quarantine_location}
                    </div>
                  )}
                  <div className="text-slate-600">
                    Type: {TYPE_NAMES[item.contamination_type] || item.contamination_type || 'Unknown'}
                  </div>
                </div>

                <div className="mt-3">
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => handleDispose(item)}
                    disabled={mutationLoading}
                    className="gap-1"
                  >
                    <Trash2 className="h-3 w-3" />
                    Dispose Now
                  </Button>
                </div>
              </AlertDescription>
            </Alert>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}
